/**
 * usePageTitle — sets `document.title` while the page is mounted (patch E1).
 *
 * Pattern:
 *
 *   function MyPage() {
 *     usePageTitle('Dashboard');
 *     return <>...</>;
 *   }
 *
 * The argument is the *page-specific* portion. The hook appends a
 * fixed " · TenderLens" suffix so users have a stable brand
 * anchor across tabs.
 *
 * Pass `null` or undefined to skip the side effect (useful when data
 * isn't yet loaded and you don't want to flash a partial title).
 *
 * The previous title is restored on unmount so that nothing leaks
 * between route transitions if the next page doesn't set its own title
 * (defensive — every page should set one, but unmount-restore is a
 * cheap safety net).
 */
import { useEffect } from 'react';

const APP_NAME = 'TenderLens';
const SEPARATOR = ' · ';

export function usePageTitle(title: string | null | undefined): void {
  useEffect(() => {
    if (!title) return undefined;
    const previous = document.title;
    document.title = formatPageTitle(title);
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * Pure formatter — exported separately so tests don't need a DOM. Also
 * lets callers compute the title string without setting it (e.g. for
 * server-rendering scenarios, though we don't currently SSR).
 */
export function formatPageTitle(title: string): string {
  return `${title}${SEPARATOR}${APP_NAME}`;
}
